import { memo, useCallback, useEffect, useState } from 'react';

import { type Board } from '@air/api/types';
import { type TreeItemProps } from '@air/component-tree';
import { PlusIcon } from '@air/design-icons';
import { Button } from '@air/primitive-button';
import { IconButton } from '@air/primitive-icon-button';
import { Tooltip } from '@air/primitive-tooltip';

import { BoardSelectCreateInput } from '~/components/BoardSelect/components/BoardSelectCreateInput';
import { BoardSelectListItem } from '~/components/BoardSelect/components/BoardSelectListItem';
import { useBoardPermissions } from '~/hooks/useBoardPermissions';
import { useFetchObjectsPermissions } from '~/hooks/useFetchObjectsPermissions';
import { useCreateNewBoard } from '~/swr-hooks/boards/useCreateNewBoard';
import { useSubBoardsList } from '~/swr-hooks/boards/useSubBoardsList';
import { canCreateBoard } from '~/utils/permissions/boardPermissions';

const CREATE_SUB_BOARD_LABEL = 'Create New Sub-Board';

export type BoardSelectBoardListItemProps = Pick<TreeItemProps, 'open'> & {
  board: Board;
  getBoardDisabledMessage?: (board: Board) => string | undefined;
  initialBoardId: Board['id'] | undefined;
  isBoardDisabled?: (board: Board) => boolean;
  isBoardExpanded?: (id: Board['id']) => boolean;
  isBoardSelected?: (board: Board) => boolean;
  onBoardOpenChange: (params: { id: Board['id']; isOpen?: boolean }) => void;
  onInitialBoardRow: (row: HTMLDivElement | null) => void;
  onSelectBoard?: (board: Board | null) => void;
  allowCreateSubBoard?: boolean;
  shouldShowSubBoards?: (id: Board['id']) => boolean;
  search?: string;
};

export const BoardSelectBoardListItem = memo(
  ({
    board,
    getBoardDisabledMessage,
    initialBoardId,
    isBoardDisabled,
    isBoardExpanded,
    isBoardSelected,
    onBoardOpenChange,
    onInitialBoardRow,
    onSelectBoard,
    allowCreateSubBoard = true,
    shouldShowSubBoards,
    search,
  }: BoardSelectBoardListItemProps) => {
    const isDisabled = isBoardDisabled?.(board);
    const isSelected = isBoardSelected?.(board);
    const isExpanded = isBoardExpanded?.(board.id);

    const [isCreatingSubBoard, setIsCreatingSubBoard] = useState(false);
    const { boardPermissions } = useBoardPermissions({ boardId: board.id });
    const { createNewBoard } = useCreateNewBoard();
    const {
      data: subBoards,
      isInitialLoading: isLoadingSubBoards,
      hasMore: hasMoreSubBoards,
      loadNextPage: loadNextSubBoardsPage,
    } = useSubBoardsList({ boardId: board.id, canFetchData: Boolean(isExpanded), params: { search } });

    useFetchObjectsPermissions({
      objects: {
        boardIds: subBoards.map((board) => board.id),
      },
    });

    const onDraftSubBoard = useCallback(() => {
      onBoardOpenChange({ id: board.id, isOpen: true });
      setIsCreatingSubBoard(true);
    }, [board.id, onBoardOpenChange]);

    const onSubBoardCreate = useCallback(
      (boardName: string) => {
        setIsCreatingSubBoard(false);

        if (boardName) {
          // biome-ignore lint/nursery/noFloatingPromises: PLA-1724 — pre-existing debt, not introduced by the icon migration
          createNewBoard({
            board: {
              title: boardName,
              parentId: board.id,
              libraryId: board.library?.id,
            },
            trackLocation: 'board-select-modal',
            shouldFollow: true,
          });
        }
      },
      [board.id, board.library?.id, createNewBoard],
    );

    useEffect(() => {
      if (shouldShowSubBoards?.(board.id)) {
        onBoardOpenChange({ id: board.id, isOpen: true });
      }
    }, [board.id, onBoardOpenChange, shouldShowSubBoards]);

    return (
      <BoardSelectListItem
        disabled={isDisabled}
        disabledMessage={getBoardDisabledMessage?.(board)}
        id={board.id}
        isExpanded={isExpanded || isCreatingSubBoard}
        isSelected={isSelected}
        // biome-ignore lint/performance/noJsxPropsBind: PLA-1724 — pre-existing debt, not introduced by the icon migration
        onOpenChange={(isOpen) => onBoardOpenChange({ id: board.id, isOpen })}
        // biome-ignore lint/performance/noJsxPropsBind: PLA-1724 — pre-existing debt, not introduced by the icon migration
        onSelect={() => onSelectBoard?.(isSelected ? null : board)}
        rowRef={initialBoardId === board.id ? onInitialBoardRow : undefined}
        suffix={
          allowCreateSubBoard &&
          !isDisabled &&
          canCreateBoard(boardPermissions) && (
            <Tooltip label={CREATE_SUB_BOARD_LABEL}>
              <IconButton
                adsIcon
                appearance="ghost"
                className="hidden group-hover/treeItem:flex"
                color="gray"
                icon={PlusIcon}
                label={CREATE_SUB_BOARD_LABEL}
                onClick={onDraftSubBoard}
                size="small"
              />
            </Tooltip>
          )
        }
        title={board.title}
      >
        {isCreatingSubBoard && (
          // biome-ignore lint/performance/noJsxPropsBind: PLA-1724 — pre-existing debt, not introduced by the icon migration
          <BoardSelectCreateInput onBlur={() => setIsCreatingSubBoard(false)} onSubmit={onSubBoardCreate} />
        )}

        {subBoards.map((subBoard) => {
          return (
            <BoardSelectBoardListItem
              allowCreateSubBoard={allowCreateSubBoard}
              board={subBoard}
              getBoardDisabledMessage={getBoardDisabledMessage}
              initialBoardId={initialBoardId}
              isBoardDisabled={isBoardDisabled}
              isBoardExpanded={isBoardExpanded}
              isBoardSelected={isBoardSelected}
              key={subBoard.id}
              onBoardOpenChange={onBoardOpenChange}
              onInitialBoardRow={onInitialBoardRow}
              onSelectBoard={onSelectBoard}
              shouldShowSubBoards={shouldShowSubBoards}
              search={search}
            />
          );
        })}

        {allowCreateSubBoard && !isCreatingSubBoard && isExpanded && !subBoards.length && !isLoadingSubBoards && (
          <Button appearance="ghost" className="mt-1" color="gray" onClick={onDraftSubBoard} size="small">
            {CREATE_SUB_BOARD_LABEL}
          </Button>
        )}

        {!isLoadingSubBoards && hasMoreSubBoards && (
          // biome-ignore lint/performance/noJsxPropsBind: PLA-1724 — pre-existing debt, not introduced by the icon migration
          <Button className="mt-1" onClick={() => loadNextSubBoardsPage()} size="small" appearance="ghost" color="gray">
            Show more
          </Button>
        )}
      </BoardSelectListItem>
    );
  },
);

BoardSelectBoardListItem.displayName = 'BoardSelectBoardListItem';
